import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity, AlertTriangle, BadgeCheck, ChevronLeft, ChevronRight,
  Eye, FileClock, KeyRound, LoaderCircle, RefreshCw,
  Search, ShieldAlert, ShieldCheck, Sparkles, UserRoundCheck, UsersRound,
} from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import PageSearchInput from "@/components/common/PageSearchInput";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { listAdminAuditEvents } from "@/lib/api";
import { fmtDateTime, timeAgo } from "@/lib/formatters";

const PAGE_SIZE = 40;
const DOMAIN_OPTIONS = [
  ["all", "All operational areas"],
  ["admin", "Identity & access"],
  ["shop", "Shops"],
  ["subscription", "Subscriptions"],
  ["payment", "Payments"],
  ["support", "Support"],
  ["sync", "Sync reliability"],
  ["referral", "Referrals"],
  ["sales_agent", "Field sales"],
];

const DOMAIN_META = {
  admin: { label: "Identity & access", icon: KeyRound, tone: "bg-violet-50 text-violet-700 border-violet-200" },
  shop: { label: "Shops", icon: BadgeCheck, tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  subscription: { label: "Subscriptions", icon: FileClock, tone: "bg-blue-50 text-blue-700 border-blue-200" },
  payment: { label: "Payments", icon: Activity, tone: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  support: { label: "Support", icon: UsersRound, tone: "bg-amber-50 text-amber-700 border-amber-200" },
  sync: { label: "Sync reliability", icon: RefreshCw, tone: "bg-sky-50 text-sky-700 border-sky-200" },
  referral: { label: "Referrals", icon: Sparkles, tone: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200" },
  sales_agent: { label: "Field sales", icon: UserRoundCheck, tone: "bg-lime-50 text-lime-700 border-lime-200" },
  platform: { label: "Platform", icon: ShieldCheck, tone: "bg-slate-50 text-slate-700 border-slate-200" },
};

function domainMeta(domain) {
  return DOMAIN_META[domain] || {
    label: humanize(domain || "platform"),
    icon: Activity,
    tone: "bg-slate-50 text-slate-700 border-slate-200",
  };
}

function humanize(value = "") {
  return String(value)
    .replace(/[._-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function actionSummary(event) {
  return humanize(event.action?.split(".").slice(1).join(".") || event.action || "activity");
}

function severityMeta(event) {
  if (event.severity === "critical") {
    return { label: "Critical", icon: ShieldAlert, tone: "border-rose-200 bg-rose-50 text-rose-700" };
  }
  if (event.severity === "elevated" || event.sensitive) {
    return { label: "Elevated", icon: AlertTriangle, tone: "border-amber-200 bg-amber-50 text-amber-700" };
  }
  return { label: "Routine", icon: ShieldCheck, tone: "border-emerald-200 bg-emerald-50 text-emerald-700" };
}

function safeMetadata(metadata) {
  if (!metadata || typeof metadata !== "object") return [];
  return Object.entries(metadata)
    .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== "")
    .map(([key, value]) => {
      const protectedValue = /(password|secret|token|session|otp)/i.test(key);
      return [key, protectedValue ? "••••••••" : typeof value === "object" ? JSON.stringify(value) : String(value)];
    });
}

export default function AuditLogs() {
  const [events, setEvents] = useState([]);
  const [metrics, setMetrics] = useState({ activity24h: 0, sensitive24h: 0, actors24h: 0, total: 0, topDomain: "" });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [risk, setRisk] = useState("all");
  const [domain, setDomain] = useState("all");
  const [windowDays, setWindowDays] = useState("7");
  const [cursorStack, setCursorStack] = useState([""]);
  const [pageIndex, setPageIndex] = useState(0);
  const [nextCursor, setNextCursor] = useState("");

  const filters = useMemo(() => ({
    cursor: cursorStack[pageIndex] || undefined,
    domain: domain === "all" ? undefined : domain,
    limit: PAGE_SIZE,
    query: query || undefined,
    risk: risk === "all" ? undefined : risk,
    window: Number(windowDays),
  }), [cursorStack, domain, pageIndex, query, risk, windowDays]);

  const loadEvents = useCallback(async (signal) => {
    setLoading(true);
    setError("");
    try {
      const payload = await listAdminAuditEvents(filters, signal);
      const items = Array.isArray(payload?.items) ? payload.items : payload?.events || [];
      setEvents(items);
      setMetrics(payload?.metrics || {});
      setTotal(Number(payload?.total ?? items.length));
      setNextCursor(payload?.nextCursor || "");
    } catch (requestError) {
      if (requestError?.name !== "AbortError") setError(requestError?.message || "Could not load audit activity");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const controller = new AbortController();
    loadEvents(controller.signal);
    return () => controller.abort();
  }, [loadEvents]);

  const resetPagination = () => {
    setCursorStack([""]);
    setPageIndex(0);
  };

  const changeFilter = (setter) => (value) => {
    setter(value);
    resetPagination();
  };

  const goNext = () => {
    if (!nextCursor) return;
    setCursorStack((previous) => [...previous.slice(0, pageIndex + 1), nextCursor]);
    setPageIndex((value) => value + 1);
  };

  const start = total ? pageIndex * PAGE_SIZE + 1 : 0;
  const end = total ? Math.min(start + events.length - 1, total) : 0;
  return (
    <div className="space-y-5" data-testid="audit-page">
      <PageHeader
        eyebrow="Security operations"
        title="Audit command center"
        subtitle="Investigate administrative activity and review protected operational changes."
        actions={<>
          <Button data-testid="audit-refresh-btn" variant="outline" onClick={() => loadEvents()} disabled={loading}>
            <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />Refresh
          </Button>
        </>}
      />

      <section className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <MetricCard icon={Activity} label="Activity · 24h" value={metrics.activity24h} helper="Recorded admin events" />
        <MetricCard icon={ShieldAlert} label="Protected changes" value={metrics.sensitive24h} helper="Review-worthy actions" accent="rose" />
        <MetricCard icon={UserRoundCheck} label="Active operators" value={metrics.actors24h} helper="Distinct actors · 24h" accent="blue" />
        <MetricCard icon={Sparkles} label="Most active area" value={metrics.topDomain ? domainMeta(metrics.topDomain).label : "No activity"} helper={`${metrics.total || 0} events retained`} compact />
      </section>

      <section className="card-elev rounded-xl p-3">
        <div className="grid gap-2 xl:grid-cols-[minmax(280px,1fr)_205px_180px_150px]">
          <PageSearchInput testId="audit-search" value={query} onSearch={(value) => { setQuery(value); resetPagination(); }} placeholder="Search actor, action, target or reference…" />
          <Select value={domain} onValueChange={changeFilter(setDomain)}>
            <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{DOMAIN_OPTIONS.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={risk} onValueChange={changeFilter(setRisk)}>
            <SelectTrigger data-testid="audit-sensitivity-filter" className="bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">All severities</SelectItem><SelectItem value="sensitive">Protected only</SelectItem><SelectItem value="routine">Routine only</SelectItem></SelectContent>
          </Select>
          <Select value={windowDays} onValueChange={changeFilter(setWindowDays)}>
            <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="1">Last 24 hours</SelectItem><SelectItem value="7">Last 7 days</SelectItem><SelectItem value="30">Last 30 days</SelectItem><SelectItem value="0">All time</SelectItem></SelectContent>
          </Select>
        </div>
      </section>

      <section className="card-elev overflow-hidden rounded-xl">
        <div className="flex items-center justify-between border-b border-[#0E5B52]/10 bg-[#FAFCFB] px-4 py-3">
          <div><p className="text-sm font-semibold text-[#064E3B]">Evidence timeline</p><p className="text-[11px] text-[#71817C]">Newest activity first · select any event to inspect its evidence</p></div>
          <Badge variant="outline" className="border-[#0E5B52]/15 bg-white text-[#0E5B52]">{total} matching</Badge>
        </div>
        {loading ? <State icon={LoaderCircle} text="Loading operational evidence…" spin /> :
          error ? <State icon={ShieldAlert} title="Audit evidence is unavailable" text={error} /> :
          !events.length ? <State icon={Search} title="No matching evidence" text="Adjust the time window or filters to broaden the investigation." /> :
          <div className="divide-y divide-[#0E5B52]/10">
            {events.map((event) => <AuditRow key={event.id} event={event} onSelect={() => setSelected(event)} />)}
          </div>}
        <div className="flex flex-col gap-3 border-t border-[#0E5B52]/10 bg-[#FAFCFB] px-4 py-3 text-xs text-[#66736F] sm:flex-row sm:items-center sm:justify-between">
          <span>Showing {start}–{end} of {total} · up to {PAGE_SIZE} per page</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={pageIndex === 0 || loading} onClick={() => setPageIndex((value) => value - 1)}><ChevronLeft className="mr-1 h-4 w-4" />Previous</Button>
            <Button size="sm" variant="outline" disabled={!nextCursor || loading} onClick={goNext}>Next<ChevronRight className="ml-1 h-4 w-4" /></Button>
          </div>
        </div>
      </section>

      <AuditDetail event={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function MetricCard({ accent = "green", compact, helper, icon: Icon, label, value }) {
  const tones = { green: "bg-emerald-50 text-emerald-700", rose: "bg-rose-50 text-rose-700", blue: "bg-sky-50 text-sky-700" };
  return <div className="card-elev min-h-[116px] rounded-xl p-4">
    <div className="flex items-start justify-between gap-2"><p className="text-[10px] font-semibold uppercase tracking-[.13em] text-[#6B7280]">{label}</p><span className={`rounded-lg p-2 ${tones[accent]}`}><Icon className="h-4 w-4" /></span></div>
    <p className={`mt-2 font-display font-semibold text-[#064E3B] ${compact ? "text-lg leading-6" : "text-3xl"}`}>{value || 0}</p>
    <p className="mt-1 text-[11px] text-[#71817C]">{helper}</p>
  </div>;
}

function AuditRow({ event, onSelect }) {
  const domain = domainMeta(event.domain);
  const severity = severityMeta(event);
  const DomainIcon = domain.icon;
  const SeverityIcon = severity.icon;
  return <button type="button" data-testid={`audit-row-${event.id}`} onClick={onSelect} className="group grid w-full gap-3 bg-white px-4 py-4 text-left transition hover:bg-[#F8FBF9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0E5B52] md:grid-cols-[44px_minmax(0,1fr)_170px_auto] md:items-center">
    <span className={`flex h-10 w-10 items-center justify-center rounded-xl border ${domain.tone}`}><DomainIcon className="h-4 w-4" /></span>
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2"><p className="truncate text-sm font-semibold text-[#064E3B]">{actionSummary(event)}</p><Badge variant="outline" className={`text-[10px] ${severity.tone}`}><SeverityIcon className="mr-1 h-3 w-3" />{severity.label}</Badge></div>
      <p className="mt-1 truncate text-xs text-[#66736F]"><span className="font-medium">{event.actorName || "System"}</span> · {event.actorRole || "System"} · {domain.label}</p>
    </div>
    <div className="md:text-right"><p className="text-xs font-medium text-[#334A43]">{fmtDateTime(event.createdAt)}</p><p className="mt-0.5 text-[11px] text-[#8A9692]">{timeAgo(event.createdAt)}</p></div>
    <span className="hidden items-center gap-1 text-xs font-medium text-[#0E5B52] opacity-0 transition group-hover:opacity-100 md:flex"><Eye className="h-4 w-4" />Inspect</span>
  </button>;
}

function AuditDetail({ event, onClose }) {
  const metadata = safeMetadata(event?.metadata);
  const domain = domainMeta(event?.domain);
  const severity = event ? severityMeta(event) : severityMeta({});
  return <Sheet open={Boolean(event)} onOpenChange={(open) => !open && onClose()}>
    <SheetContent className="w-full overflow-y-auto border-[#0E5B52]/15 bg-white p-0 sm:max-w-xl">
      {event && <>
        <div className="border-b border-[#0E5B52]/10 bg-gradient-to-br from-[#EFF8F3] via-white to-[#F7FBF9] p-6">
          <SheetHeader className="pr-8">
            <div className="mb-3 flex items-center gap-2"><Badge variant="outline" className={domain.tone}>{domain.label}</Badge><Badge variant="outline" className={severity.tone}>{severity.label}</Badge></div>
            <SheetTitle className="font-display text-2xl text-[#064E3B]">{actionSummary(event)}</SheetTitle>
            <SheetDescription>{fmtDateTime(event.createdAt)} · {timeAgo(event.createdAt)}</SheetDescription>
          </SheetHeader>
        </div>
        <div className="space-y-6 p-6">
          <DetailSection title="Attribution">
            <Detail label="Operator" value={event.actorName || "System"} />
            <Detail label="Operational role" value={event.actorRole || "System"} />
            <Detail label="Actor ID" value={event.actorId || "System generated"} mono />
          </DetailSection>
          <DetailSection title="Recorded action">
            <Detail label="Action key" value={event.action} mono />
            <Detail label="Target type" value={event.targetType || "—"} />
            <Detail label="Target reference" value={event.targetRef || "—"} mono />
            {event.targetId && <Detail label="Target ID" value={event.targetId} mono />}
          </DetailSection>
          <DetailSection title="Evidence payload" helper="Credentials and session identifiers are masked in the console and exports.">
            {metadata.length ? metadata.map(([key, value]) => <Detail key={key} label={humanize(key)} value={value} mono />) : <p className="text-sm text-[#71817C]">No additional evidence was attached to this event.</p>}
          </DetailSection>
          <div className="rounded-xl border border-[#0E5B52]/10 bg-[#F6FAF7] p-4">
            <p className="flex items-center gap-2 text-xs font-semibold text-[#064E3B]"><ShieldCheck className="h-4 w-4" />Evidence reference</p>
            <p className="mt-2 break-all font-mono text-[11px] text-[#66736F]">{event.id}</p>
          </div>
        </div>
      </>}
    </SheetContent>
  </Sheet>;
}

function DetailSection({ children, helper, title }) {
  return <section><div className="mb-3"><h3 className="text-xs font-semibold uppercase tracking-[.12em] text-[#53635E]">{title}</h3>{helper && <p className="mt-1 text-[11px] text-[#8A9692]">{helper}</p>}</div><div className="overflow-hidden rounded-xl border border-[#0E5B52]/10 bg-white">{children}</div></section>;
}

function Detail({ label, mono, value }) {
  return <div className="grid gap-1 border-b border-[#0E5B52]/10 px-4 py-3 last:border-b-0 sm:grid-cols-[150px_1fr]"><span className="text-xs text-[#71817C]">{label}</span><span className={`break-all text-sm text-[#173D34] ${mono ? "font-mono text-xs" : "font-medium"}`}>{String(value)}</span></div>;
}

function State({ icon: Icon, spin, text, title }) {
  return <div className="flex min-h-56 flex-col items-center justify-center px-6 text-center"><span className="mb-3 rounded-xl bg-[#F0F6F1] p-3 text-[#0E5B52]"><Icon className={`h-5 w-5 ${spin ? "animate-spin" : ""}`} /></span>{title && <p className="font-semibold text-[#064E3B]">{title}</p>}<p className="mt-1 max-w-md text-sm text-[#66736F]">{text}</p></div>;
}
